import { useState } from 'react';
import {
  Autocomplete,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { useQuery } from '@apollo/client/react';
import { ArrowDropDown } from '@mui/icons-material';

import { tss } from 'tss-react/mui';

import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';

import { FilePicker } from '@/modules/files/components/file-picker/FilePicker';
import type { RootOwner } from '@/modules/files/components/file-picker/FilePicker';
import { extractConnectionProps } from '@/utils/queries/queryExtracts';
import { KeyBadge } from '@/components/KeyBadge/KeyBadge';
import { GroupOrServiceAvatar } from '@/components/Avatar/GroupOrServiceAvatar';
import { Or2ListItem } from '@/components/data-display/Or2ListItem';
import type { FILE_MENU_ITEM_FRAGMENT } from '@/modules/files/components/file-picker/FileMenuItem';
import { Or2Dialog } from '@/components/feedback/Or2Dialog';
import { Or2Button } from '@/components/button/Or2Button';
import { AI_ASSISTANT_SUPPORTED_FILE_TYPES } from '@/utils/file/aiAssistantSupportedFileTypes';

const useStyles = tss.create(({ theme }) => ({
  ksgBadge: {
    border: `2px solid ${theme.palette.background.default}`,
    backgroundColor: theme.palette.background.default,
  },
  badge: {
    marginRight: theme.spacing(2),
  },
}));

const ASSISTANT_FILE_PICKER_DIALOG_GROUP_FRAGMENT = graphql(`
  fragment assistantFilePickerDialogGroupFields on Group {
    _id
    name
    iconUrl
    hasKsg
    type {
      hasFiles
    }
  }
`);

const ASSISTANT_FILE_PICKER_GROUPS_QUERY = graphql(`
  query AssistantFilePickerGroups {
    currentUser {
      _id
      shortcutGroupsWithServiceAdminPermissionsConnection(
        first: null
        after: null
        filterBy: {}
      ) {
        edges {
          node {
            ...assistantFilePickerDialogGroupFields
          }
        }
      }
    }
  }
`);

interface AssistantFilePickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (fileId: string | null | undefined) => void;
}

export const AssistantFilePickerDialog = ({
  open,
  onClose,
  onSelect,
}: AssistantFilePickerDialogProps) => {
  const { classes } = useStyles();
  const [step, setStep] = useState<'group' | 'file'>('group');
  const [selectedGroup, setSelectedGroup] = useState<RootOwner | null>(null);
  const [parentId, setParentId] = useState<string | null | undefined>(null);

  const { data, loading } = useQuery(ASSISTANT_FILE_PICKER_GROUPS_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip: !open || step !== 'group',
  });

  const { currentUser } = data ?? {};
  const { shortcutGroupsWithServiceAdminPermissionsConnection } =
    currentUser ?? {};

  const groupOptions = extractConnectionProps(
    shortcutGroupsWithServiceAdminPermissionsConnection,
  )
    .map((group) => ({
      ...group,
      hasFiles: group.type?.hasFiles ?? false,
    }))
    .filter((g) => g.hasFiles);

  const handleClose = () => {
    onClose();
    setStep('group');
    setSelectedGroup(null);
    setParentId(null);
  };

  const handleGroupSelect = (
    group: DocumentType<
      typeof ASSISTANT_FILE_PICKER_DIALOG_GROUP_FRAGMENT
    > | null,
  ) => {
    if (group && group._id && group.name) {
      setSelectedGroup({
        _id: group._id,
        type: 'Group',
      });
      setStep('file');
      setParentId(null);
    }
  };

  const handleSelect = (
    fileId: string | null | undefined,
    _ownerId: string,
    _file: DocumentType<typeof FILE_MENU_ITEM_FRAGMENT> | null | undefined,
  ) => {
    onSelect(fileId);
    setStep('group');
    setSelectedGroup(null);
    setParentId(null);
  };

  return (
    <Or2Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      {step === 'group' && (
        <>
          <DialogTitle>Select a Group</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please select the group where the file is located.
            </Typography>
            <Autocomplete
              id="group-select-autocomplete"
              options={groupOptions}
              loading={loading}
              getOptionLabel={(option) => option?.name ?? ''}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              onChange={(_event, newValue) => handleGroupSelect(newValue)}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                return (
                  <Or2ListItem dense key={option._id} {...rest}>
                    <ListItemIcon>
                      <KeyBadge
                        keyAvatarProps={{
                          size: 12,
                          className: classes.ksgBadge,
                        }}
                        className={classes.badge}
                        hasKsg={option.hasKsg ?? false}
                      >
                        <GroupOrServiceAvatar
                          iconUrl={option?.iconUrl}
                          groupOrServiceName={option.name}
                          size={32}
                        />
                      </KeyBadge>
                    </ListItemIcon>
                    <ListItemText
                      slotProps={{ primary: { variant: 'subtitle1' } }}
                    >
                      {option?.name}
                    </ListItemText>
                  </Or2ListItem>
                );
              }}
              popupIcon={
                loading ? (
                  <CircularProgress disableShrink size={16} />
                ) : (
                  <ArrowDropDown />
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search for a group"
                  placeholder="Start typing..."
                  variant="outlined"
                  margin="normal"
                  fullWidth
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Or2Button onClick={handleClose}>Cancel</Or2Button>
          </DialogActions>
        </>
      )}

      {step === 'file' && selectedGroup && (
        <FilePicker
          selectionMode="file"
          preventRootSelection
          title="Select a file"
          rootOwner={selectedGroup}
          parentId={parentId}
          onClose={handleClose}
          onSelect={handleSelect}
          fileActionName="Select"
          parentActionName="Select"
          onChangeParent={setParentId}
          displayCreateFolderButton={false}
          disableItemWithReason={(file) => {
            const fileExtension =
              file.filename?.split('.').pop()?.toLowerCase() ?? '';

            if (
              !file.folder &&
              !AI_ASSISTANT_SUPPORTED_FILE_TYPES.includes(fileExtension)
            ) {
              return 'Unsupported file type';
            }

            return null;
          }}
          onBack={() => {
            setStep('group');
            setSelectedGroup(null);
            setParentId(null);
          }}
        />
      )}
    </Or2Dialog>
  );
};
